<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" @show="onShow" @hidden="onHidden" :show-confirmation="false"
                   :title="$t(`Payments for {no}`, {no: settlement.settlement_no})"
                   hide-footer :content-in-form="false" :is-loading="loading" body-class="pt-0">
      <artisio-table v-if="settlement && settlement.settlement_payments" :items="settlement.settlement_payments"
                     :fields="tableFields"
                     :index="false">
        <template slot="cell(type)" slot-scope="{item}">
          {{ item.payment.type | removeUnderscores | toUpperCaseFirst }}
        </template>
        <template slot="cell(method)" slot-scope="{item}">
          {{ item.payment.method | asPaymentMethod }}
        </template>
        <template slot="cell(amount)" slot-scope="{item}">
          {{ item.amount | asCurrency(item.payment.currency.symbol) }}
        </template>
        <template slot="cell(date)" slot-scope="{item}">
          {{ item.payment.date | toDate }}
        </template>
        <template slot="cell(bank_account)" slot-scope="{item}">
          {{ item.payment.bank_account }}
        </template>
        <template slot="cell(description)" slot-scope="{item}">
          {{ item.payment.description }}
        </template>
        <template slot="cell(reference_number)" slot-scope="{item}">
          {{ item.payment.reference_number }}
        </template>
        <template slot="cell(beneficiary)" slot-scope="{item}">
          {{ getBeneficiaryName(item) }}
        </template>
        <template slot="cell(export)" slot-scope="{item}" v-if="isExportFieldExist && modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL">
          <a href="javascript:void(0)" @click="onFinancialExportSettlementClick(item)">
            {{ item.financial_export ? item.financial_export.export_no : '' }}
          </a>
        </template>
        <template slot="cell(check_no)" slot-scope="{item}">
          {{ item.check_number ? item.check_number : '' }}
        </template>
        <template slot="cell(check_status)" slot-scope="{item}">
          <span v-if="item.check_status" v-as-status="item.check_status">
            {{ item.check_status | toUpperCaseFirst }}
          </span>
        </template>
        <template slot="cell(payee)" slot-scope="{item}">
          {{ item.payee ? item.payee : '' }}
        </template>
        <template slot="cell(batch_id)" slot-scope="{item}">
          {{ item.batch_id ? item.batch_id : '' }}
        </template>
        <template slot="cell(batch_payment)" slot-scope="{item}">
          <div v-if="item.payment.batch_payment">
            <a href="javascript:void(0)" @click="onBatchInfoClick(item)" class="d-block" v-if="modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL"">
              {{ item.payment.batch_payment.payment_no }}
            </a>
            <div v-else>
              {{ item.payment.batch_payment.payment_no }}
            </div>

            <span v-if="item.payment.batch_payment.status" v-as-status="item.payment.batch_payment.status">
              {{ item.payment.batch_payment.status | removeUnderscores | toUpperCaseFirst }}
            </span>
          </div>
        </template>
        <template slot="cell(reconciled)" slot-scope="{item}">
          <div class="d-flex justify-content-center">
            <b-form-checkbox :checked="item.payment.reconciled" @change="toggleReconciled(item)" v-if="modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL"/>
            <b-form-checkbox v-else :checked="item.payment.reconciled" disabled/>
          </div>
        </template>
        <template slot="cell(comment)" slot-scope="{item}">
          {{ item.comment ? item.comment : '' }}
        </template>
        <template slot="cell(vendor_group_account)" slot-scope="{item}" v-if="vendorBuyerGroupAccountsEnabled">
          {{ settlement.auction ? settlement.auction.vendor_group_account : '' }}
        </template>
        <template slot="cell(actions)" slot-scope="{item: payment}" v-if="modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL">
          <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v" class="icon"/>
            </template>

            <b-dd-item v-permission="'change_settlement'"
                       variant="primary" size="sm" class="mr-2" @click="onPaymentEditClick(payment)"
                       :disabled="!!(isDebitedSettlement || payment.check_uuid || payment.financial_export || payment.batch_id)">
              <font-awesome-icon icon="pen" style="font-size: .9em" class="mr-1"/>
              {{ $t('Edit Payment') }}
            </b-dd-item>
            <b-dd-item v-permission="'change_settlement'"
                       variant="danger" size="sm" class="mr-2" @click="onPaymentDeleteClick(payment)"
                       :disabled="!!(isDebitedSettlement || payment.check_status === 'cancelled' || payment.financial_export || payment.batch_id)">
              <font-awesome-icon icon="trash" style="font-size: .9em" class="mr-1"/>
              {{ $t('Delete Payment') }}
            </b-dd-item>

            <b-dd-item
              variant="primary" size="sm" class="mr-2" @click="onPdfViewClick(payment)"
              :disabled="!payment.check_uuid">
              <font-awesome-icon icon="download" style="font-size: .9em" class="mr-1"/>
              {{ $t('View Check') }}
            </b-dd-item>

            <refund-payment-d-d-item :settlement-payment="payment" @success="onRefundSuccess"/>

          </b-dropdown>

        </template>
      </artisio-table>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {SOURCE_SETTLEMENTS_TABLE, SOURCE_BATCH_MARK_UNSOLD_MODAL} from "../constants";
  import SettlementService from "@/modules/settlements/SettlementService";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import ExportsService from "@/core/services/exports.service";
  import RefundPaymentDDItem from "@/modules/settlement-payments/RefundPaymentDDItem";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapActions: settlementPaymentMapActions} = createNamespacedHelpers('settlementPayments');

  export default {
    name: "PaymentListModal",
    components: {RefundPaymentDDItem, ArtisioTable, ArtisioModal},
    data() {
      return {
        loading: false,
        showConfirmation: true,
        SOURCE_BATCH_MARK_UNSOLD_MODAL: SOURCE_BATCH_MARK_UNSOLD_MODAL,
      }
    },
    computed: {
      ...mapStateGlobal(['vendorBuyerGroupAccountsEnabled', 'paymentBankAccounts']),
      ...mapState({
        modal: state => state.paymentListModal,
        settlement: state => state.paymentListModal.settlement,
      }),
      isExportFieldExist() {
        return this.$hasPermission('enable_accounting_export')
      },
      tableFields() {
        let tableFields = [
          {key: "type", label: this.$i18n.t('Type')},
          {key: "method", label: this.$i18n.t('Method')},
          {key: "amount", label: this.$i18n.t('Amount')},
          {key: "date", label: this.$i18n.t('Date')},
          {key: "bank_account", label: this.$i18n.t('Bank Account')},
          {key: "description", label: this.$i18n.t('Description')},
          {key: "reference_number", label: this.$i18n.t('Reference No.')},
          ...(this.settlement.settlement_payments && this.settlement.settlement_payments.some(settlement => settlement.payment.beneficiary_relation)
            ? [{key: "beneficiary", label: this.$i18n.t('Beneficiary')}]
            : []),
          {key: "export", label: this.$i18n.t('Export')},
          {key: "check_no", label: this.$i18n.t('Check No.')},
          {key: "check_status", label: this.$i18n.t('Check Status')},
          {key: "payee", label: this.$i18n.t('Payee')},
          {key: "batch_id", label: this.$i18n.t('BACS ID')},
          {key: "reconciled", label: this.$i18n.t('Reconciled')},
          {key: "comment", label: this.$i18n.t('Comment')},
          {key: "vendor_group_account", label: this.$i18n.t('Vendor Group Account')},
          {key: "batch_payment", label: this.$i18n.t('Batch ID')},
          {key: "actions", label: this.$i18n.t('Actions')}
        ];

        if (['cancelled', 'debited'].includes(this.settlement.status)) {
          tableFields = tableFields.filter(f => f.key !== 'actions')
        }

        if (this.paymentBankAccounts.length === 0) {
          tableFields = tableFields.filter(f => f.key !== 'bank_account')
        }

        if (!this.isExportFieldExist) {
          tableFields = tableFields.filter(f => f.key !== 'export')
        }

        if (this.modal.source === this.SOURCE_BATCH_MARK_UNSOLD_MODAL) {
          tableFields = tableFields.filter(f => !['export', 'actions'].includes(f.key));
        }

        if (!this.vendorBuyerGroupAccountsEnabled) {
          tableFields = tableFields.filter(f => f.key !== 'vendor_group_account')
        }

        return tableFields;
      },
      isDebitedSettlement() {
        return this.settlement.status === 'debited';
      },
    },
    methods: {
      ...mapActions(['hidePaymentListModal', 'showPaymentModal', 'getTableData',
        'updatePaymentListModalSettlement', 'updatePaymentModalObject']),
      ...settlementPaymentMapActions({
        settlementPaymentsUpdateTableSource: 'updateTableSource',
        settlementPaymentsUpdateSearchModel: 'updateSearchModel',
      }),
      async onFinancialExportSettlementClick(item) {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t(`Financial Export ${item.financial_export.export_no}`,),
        });
        const {body, success} = await ExportsService.exportFinancialHistory({
          uuid: item.financial_export.uuid
        })

        if (success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: body.url,
          })
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);

          let errorMessage = "";
          if (body.non_field_errors) {
            errorMessage = body.non_field_errors[0];
          } else {
            errorMessage = body;
          }
          this.$errorDialog(errorMessage);
        }
      },
      onShow() {
        this.showConfirmation = true
      },
      onHidden() {
        this.hidePaymentListModal();
      },
      getBeneficiaryName(item) {
        const receipts = this.settlement.receipts;
        const display_name = item.payment.beneficiary_relation ? item.payment.beneficiary_relation.related_customer.display_name : null;
        if (display_name) {
          const receipt = receipts.find(receipt => receipt.uuid === item.payment.beneficiary_relation.object_uuid);
          return `${receipt.receipt_no} - ${display_name}`
        }
        return null;
      },
      async onPaymentEditClick(settlementPayment) {
        await SettlementService.onEditPayment(this, settlementPayment, this.modal.source, this.settlement)
      },
      async onPaymentDeleteClick(settlementPayment) {
        await SettlementService.onDeletePayment(this, settlementPayment, this.modal.source)
      },
      async toggleReconciled(settlement_payment) {
        const {payment: {uuid, reconciled}} = settlement_payment;

        const response = await SettlementService.partialUpdatePayment(uuid, {
          reconciled: !reconciled,
        });

        if (response && response.success) {
          await this.getTableData();
          this.updatePaymentListModalSettlement();
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        }
      },
      async onPdfViewClick(item) {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t('Check {check_no}', {check_no: item.check_number})
        });
        let response = await SettlementService.getSettlementCheckPdfData(item.check_uuid, false);
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: response.body.pdf_url,
            filename: 'Check'
          })
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
          let errorMessage = "";
          if (response.body.non_field_errors) {
            errorMessage = response.body.non_field_errors[0];
          } else {
            errorMessage = response.body;
          }
          this.$errorDialog(errorMessage);
        }
      },
      async onRefundSuccess() {
        await this.getTableData()
        await this.updatePaymentListModalSettlement()
      },
      async onBatchInfoClick(settlementPayment) {
        await this.settlementPaymentsUpdateTableSource({source: 'finance'});
        await this.settlementPaymentsUpdateSearchModel({searchModel: {batch_payment_id: settlementPayment.payment.batch_payment.payment_no}});
        this.hidePaymentListModal();
        this.$router.push({name: 'settlementPayments'})
      }
    }
  }
</script>

<style>
  .table-wrapper {
    min-height: 150px;
  }
</style>
